<template>
  <div class="vh-category-panel" :class="{ notransition: demoMode }">
    <h2 class="basic-sub-title">
      Details des critères
    </h2>
    <CloseButton @click.native="$emit('close')" />

    <Tabs v-model="focusedCategoryTab" :tabs="availableTabsText" class="vhcp-content">
      <div v-if="!loading" ref="content" class="vhcp-selected-cat-panel-wrapper">

        <!-- Filter options -->
        <ExpandableSettingsItem
          ref="filters"
          :title="$t('message.VH_OPTIONS_FILTERS')"
          class="filters"
          default-expand
        >
          <div>
            <EditableLabelRadio
              v-model="groupFilter"
              :items="groupFilterItems"
              :legend="`${$t('message.VH_DISPLAY_GROUPS')}${$t('message.TWO_DOTS')}`"
              label-key="name"
            />
            <EditableLabelRadio
              v-if="focusedCategoryTab === 0"
              v-model="categoryFilter"
              :items="categoryFilterItems"
              multiple
              :legend="`${$t('message.VH_DISPLAY_CATEGORY')}${$t('message.TWO_DOTS')}`"
            />
            <Checkbox v-model="allSenseiDisplayed" :title="$t('message.VH_SHOW_ALL_SENSEI_HINTS')" class="sensei-cb" />
            <!-- Criticality filter -->
            <div>
              <span class="legend">{{ `${$t('message.VH_FILTER_BY_CRITICALITY')}${$t('message.TWO_DOTS')}` }}</span>
              <span class="field-hint ml10">{{ $t('message.VH_CRITICALITY_TABLE_USAGE') }}</span>
            </div>
            <div class="criticality-table">
              <div v-for="(section, sidx) in criticalitySections" :key="sidx" class="criticality-row">
                <div
                  v-for="(criticality, idx) in section"
                  :key="idx"
                  class="criticality-col"
                  :style="criticality.style"
                >
                  <ExpandableSettingsItem
                    ref="criticalityFilters"
                    :title="`${criticality.criteria.length} ${criticality.title}`"
                    refresh-on-resize
                    @update="criticalityFilterUpdate"
                  >
                    <div>
                      <div
                        v-for="criterion in criticality.criteria"
                        :key="criterion.key"
                        class="criticality-criterion"
                        @click="highlightCriterion(criterion)"
                      >
                        {{ `- ${$t(`message.${criterion.titleTr}`)}` }}
                      </div>
                    </div>
                  </ExpandableSettingsItem>

                  <Checkbox
                    v-model="criticalityFilter[criticality.id]"
                    no-animation
                    @input="criticalityFilter = criticalityFilter"
                  />
                </div>
              </div>
            </div>
          </div>
        </ExpandableSettingsItem>

        <!-- All categories -->
        <div v-if="focusedCategoryTab === 0">
          <div v-for="category in filteredCategoriesAndCriteria" :key="category.key">
            <h3 class="category-title">
              {{ $t(`message.${category.titleTr}`) }}
            </h3>
            <div class="category-hint">
              <!-- eslint-disable-next-line vue/no-v-html -->
              <div v-html="category.senseiIntro" />
            </div>
            <div class="vhcp-criteria-wrapper">
              <div v-if="category.criteria.length === 0" class="field-hint">
                {{ $t('message.VH_NO_FILTERED_CRITERIA') }}
              </div>
              <VHCriterionTable
                v-for="(criterion, index) in category.criteria"
                :ref="`table-${criterion.key}`"
                :key="criterion.key"
                :class="{ highlight: highlightedCriterionKeys.includes(criterion.key) }"
                :criterion="criterion"
                :index="index"
                :max-height="tableMaxHeight"
                :min-height="tableMinHeight"
                :groups="groupsFiltered"
              />
            </div>
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div class="category-hint outro" v-html="category.senseiOutro" />
          </div>
        </div>

        <!-- Single category -->
        <div v-else-if="focusedCategory">
          <h3 class="category-title">
            {{ $t(`message.${focusedCategory.titleTr}`) }}
          </h3>
          <div class="category-hint">
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div v-html="focusedCategory.senseiIntro" />
          </div>
          <div class="vhcp-criteria-wrapper">
            <div v-if="focusedCategory.criteria.length === 0" class="field-hint">
              {{ $t('message.VH_NO_FILTERED_CRITERIA') }}
            </div>
            <VHCriterionTable
              v-for="(criterion, index) in focusedCategoryWithCriteria.criteria"
              :ref="`table-${criterion.key}`"
              :key="criterion.key"
              :class="{ highlight: highlightedCriterionKeys.includes(criterion.key) }"
              :criterion="criterion"
              :index="index"
              :max-height="tableMaxHeight"
              :min-height="tableMinHeight"
              :groups="groupsFiltered"
            />
          </div>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div class="category-hint outro" v-html="focusedCategory.senseiOutro" />
        </div>
      </div>
    </Tabs>
  </div>
</template>

<script>
/**
 * Vitality Hub category panel.
 * Part of the vitality-hub component.
 * Displays all available criteria categories to evaluate teams' vitality
 *
 */

import $ from 'jquery';
import { cloneDeep, isEqual } from 'lodash';
// Vuex
import { createNamespacedHelpers } from 'vuex';
import { SHOW_SENSEI_CRITERIA } from '@/store/modules/vitality/mutation-types';
// Components
import Checkbox from '@/components/checkbox.vue';
import CloseButton from '@/components/close-button.vue';
import EditableLabelRadio from '@/components/editable-label-radio.vue';
import ExpandableSettingsItem from '@/components/expandable-settings-item.vue';
import Tabs from '@/components/tabs.vue';
import VHCriterionTable from '@/components/vitality-hub/criterion-table.vue';
// Utils
import { mainDelay } from '@/utils/expandable';
import { vitalityThresholds } from '@/utils/vhub-utils';
import { VitalityCategory } from '@/utils/classes/VitalityCategory';

const { mapState, mapGetters, mapMutations } = createNamespacedHelpers('vitality');

export default {
  name: 'VHCategoryPanel',

  components: {
    Checkbox,
    CloseButton,
    EditableLabelRadio,
    ExpandableSettingsItem,
    Tabs,
    VHCriterionTable,
  },

  data() {
    return {
      VitalityCategory,
      // Height boundaries in pixels
      tableMinHeight: 145,
      panelHeight: 0,
      // Highlight specific tables
      highlightedCriterionKeys: [],
      unhighlightTimeouts: [],
      mounted: false,
    };
  },

  computed: {
    /** Array: Categories to display */
    filteredCategories() {
      return VitalityCategory.all.filter(cat => this.categoryFilter.includes(cat.key));
    },
    criticalityFilteredKeys() {
      return this.criteriaPerCriticality.reduce(
        (keys, criticality) => (this.criticalityFilter[criticality.id]
          ? keys.concat(criticality.criteria.map(c => c.key))
          : keys
        ),
        [],
      );
    },
    /** Array: Same as above with filtered criteria */
    filteredCategoriesAndCriteria() {
      const categories = cloneDeep(this.filteredCategories);
      return categories.map((cat) => {
        cat.criteria = cat.criteria.filter(criterion => this.criticalityFilteredKeys.includes(criterion.key));
        return cat;
      });
    },
    /** Array: Categories to display */
    filteredCriteria() {
      const allCriteria = this.filteredCategories.reduce((criteria, cat) => criteria.concat(cat.criteria), []);
      return allCriteria;
    },
    /** Object: Item of availableTabs */
    focusedTab() {
      return this.availableTabs[this.focusedCategoryTab];
    },
    /** Array: Tabs that user can click to show a category */
    availableTabs() {
      return [
        { key: 'all', title: this.$t('message.VH_DISPLAY_CRITERIA_ALL') },
        ...VitalityCategory.all.map(c => ({ key: c.key, title: this.$t(`message.${c.titleTr}`) })),
      ];
    },
    /** Array: Tabs that user can click to show a category */
    availableTabsText() {
      return this.availableTabs.map(tab => tab.title);
    },
    /** VitalityCategory: If Tabs focu anything else than 'all', focused category */
    focusedCategory() {
      return VitalityCategory.all.find(cat => cat.key === this.focusedTab.key);
    },
    /** VitalityCategory: Same as above with filtered criteria */
    focusedCategoryWithCriteria() {
      const cat = cloneDeep(this.focusedCategory);
      cat.criteria = cat.criteria.filter(c => this.criticalityFilteredKeys.includes(c.key));
      return cat;
    },
    /** Number: Maximum height in pixels of each table */
    tableMaxHeight() {
      return this.panelHeight / 2;
    },
    /** Object: CSS style of the table */
    tableStyle() {
      // The heigh of the tables must be calculated to prevent having bigger tables than required
      // And to harmonize the height of the charts when "converting" a table to a chart
      // Height of each row + table head
      const preferredHeight = (this.teamGroups.length + 1) * (2.8 * this.$store.getters.fontSize + 15);
      const maxed = Math.min(this.panelHeight / 2, preferredHeight);
      return {
        height: `${Math.max(maxed, this.minTableHeight)}px`,
      };
    },
    /** Boolean: All the sensei hints should be displayed (for each criterion) */
    allSenseiDisplayed: {
      get() {
        return !this.filteredCriteria.some(crit => this.showSenseiForCriterion[crit.key] === false);
      },
      set(show) {
        this.SHOW_SENSEI_CRITERIA({ criteria: this.filteredCriteria, show });
      },
    },
    /** Array: Selectable items in the groups filter */
    groupFilterItems() {
      return [
        { id: 'all', name: this.$t('message.ALLS') },
        ...this.teamGroups.map(g => ({ ...g, id: g.key })),
      ];
    },
    /** Array: Selectable items in the groups filter */
    categoryFilterItems() {
      return VitalityCategory.all.map(c => ({ ...c, id: c.key, label: this.$t(`message.${c.titleTr}`) }));
    },
    /** Array: Groups whose information must be displayed in the tables */
    groupsFiltered() {
      return this.groupFilter === 'all'
        ? this.teamGroups
        : this.teamGroups.filter(group => group.key === this.groupFilter);
    },
    /** Array: Array of 4 objects. One object per criticality. See group-vitality-tile */
    criteriaPerCriticality() {
      const len = vitalityThresholds.length;
      const borderRadiuses = ['top-left', 'bottom-left', 'top-right', 'bottom-right'];
      const borders = [[1, 0, 0, 1], [0, 0, 1, 1], [1, 1, 0, 0], [0, 1, 1, 0]];
      const output = vitalityThresholds.map((t, i) => ({
        ...t,
        title: this.$t(t.titleTr),
        criteria: [],
        style: {
          background: t.lighterColor,
          borderColor: new Array(len).fill('#aaa').map((b, j) => (borders[i][j] ? t.color : b)).join(' '),
          borderWidth: new Array(len).fill('1px').map((b, j) => (borders[i][j] ? '2px' : b)).join(' '),
          [`border-${borderRadiuses[i]}-radius`]: '0.5em',
        },
      }));
      const group = this.groupFilter === 'all'
        ? this.allTeamsObject
        : this.teamGroups.find(g => g.key === this.groupFilter);
      // Check all criteria within all categories
      const categoriesToCheck = this.focusedCategoryTab === 0 ? this.filteredCategories : [this.focusedCategory];
      categoriesToCheck.forEach((category) => {
        category.criteria.forEach((criterion) => {
          for (let i = 0; i < len; i++) {
            if (
              group[criterion.scoresKey]
              && group[criterion.scoresKey][0].score <= vitalityThresholds[i].score
            ) {
              output[i].criteria.push(criterion);
              break;
            }
          }
        });
      });
      return output;
    },
    /** Array: criteriaPerCriticality split into two sections */
    criticalitySections() {
      if (!this.criteriaPerCriticality) {
        return [];
      }
      return [
        [this.criteriaPerCriticality[0], this.criteriaPerCriticality[2]],
        [this.criteriaPerCriticality[1], this.criteriaPerCriticality[3]],
      ];
    },
    ...mapState([
      'teamGroups',
      'allTeamsObject',
      'showSenseiForCriterion',
    ]),
    ...mapGetters([
      'categoryHint',
      'loading',
    ]),
    // ============
    // Query params
    // ============
    /** Number: focused index in Tabs component */
    focusedCategoryTab: {
      get() {
        const focusedTab = this.$route.query.vhcpFocusedCategoryTab;
        const idx = this.availableTabs.findIndex(cat => cat.key === focusedTab);
        if (idx >= 0) {
          return idx;
        }
        return 0;
      },
      set(tabIndex) {
        const query = Object.assign({}, this.$route.query);
        query.vhcpFocusedCategoryTab = tabIndex === 0 ? undefined : this.availableTabs[tabIndex].key;
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
    /** String: Key of the selected group to filter. 'all' if no filter */
    groupFilter: {
      get() {
        const key = this.$route.query.vhcpGroupFilter;
        if (this.groupFilterItems.some(item => item.id === key)) {
          return key;
        }
        return 'all';
      },
      set(newFilter) {
        const query = Object.assign({}, this.$route.query);
        query.vhcpGroupFilter = newFilter === 'all' ? undefined : newFilter;
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
    /** Array: Categories filter */
    categoryFilter: {
      get() {
        let filter = this.$route.query.vhcpCategoryFilter;
        if (!filter) {
          return this.categoryFilterItems.map(i => i.id);
        }
        filter = filter.split(',');
        return filter.filter(item => this.categoryFilterItems.some(c => c.key === item));
      },
      set(newVal) {
        const query = Object.assign({}, this.$route.query);
        query.vhcpCategoryFilter = isEqual(this.categoryFilterItems.map(i => i.id).sort(), newVal.sort())
          ? undefined
          : newVal.join(',');
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
    /** Array: of booleans. Whether the criticality at index x should be displayed */
    criticalityFilter: {
      get() {
        let filter = this.$route.query.vhcpCriticalityFilter;
        if (filter) {
          filter = filter.split(',');
          if (filter.length === vitalityThresholds.length) {
            return filter.map(isTrue => isTrue === 'true');
          }
        }
        return new Array(vitalityThresholds.length).fill(true);
      },
      set(newVal) {
        const query = Object.assign({}, this.$route.query);
        query.vhcpCriticalityFilter = isEqual(newVal, new Array(vitalityThresholds.length).fill(true))
          ? undefined
          : newVal.map(isTrue => (isTrue ? 'true' : 'false')).join(',');
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
  },

  watch: {
    focusedCategoryTab() {
      this.updateCriticalityFilters();
    },
    categoryFilter() {
      this.updateCriticalityFilters();
    },
    groupFilter() {
      this.updateCriticalityFilters();
    },
  },

  updated() {
    this.panelHeight = this.$el.offsetHeight;
  },

  mounted() {
    this.panelHeight = this.$el.offsetHeight;
    this.$nextTick(() => { this.mounted = true; });
  },

  beforeDestroy() {
    this.unhighlightTimeouts.forEach((timeoutWrapper) => {
      clearTimeout(timeoutWrapper.timeout);
    });
  },

  methods: {
    /** Handle all the expandable updates in a single handler after a timeout */
    criticalityFilterUpdate() {
      clearTimeout(this.filterExpandTimeout);
      this.filterExpandTimeout = setTimeout(() => {
        this.$refs.filters.itemExpand(true);
      }, mainDelay);
    },
    updateCriticalityFilters() {
      if (this.$refs.criticalityFilters) {
        this.$nextTick(() => {
          this.$refs.criticalityFilters.forEach((comp) => {
            comp.itemExpand(true);
          });
        });
      }
    },
    /** Scroll to the criterion table at index */
    scrollToCriterion(criterion) {
      const table = this.$refs[`table-${criterion.key}`];
      if (table && table[0] && table[0].$el) {
        $(this.$refs.content).stop().animate({ scrollTop: table[0].$el.offsetTop - 100 }, 500);
      }
    },
    /**
     * Scroll to the criterion table if it exists and highlight it
     * @param  {String} criterionKey - value of one of criterion.key
     */
    highlightCriterion(criterion) {
      this.scrollToCriterion(criterion);
      if (!this.highlightedCriterionKeys.includes(criterion.key)) {
        this.highlightedCriterionKeys.push(criterion.key);
      }
      const sameKeyTimeoutWrapper = this.unhighlightTimeouts.find(({ key }) => key === criterion.key);
      if (sameKeyTimeoutWrapper) {
        // If a timeout has already been defined, update it
        clearTimeout(sameKeyTimeoutWrapper.timeout);
        sameKeyTimeoutWrapper.timeout = setTimeout(() => { this.unhighlightHandler(criterion.key); }, 5000);
      } else {
        // Else, push new one
        this.unhighlightTimeouts.push({
          key: criterion.key,
          timeout: setTimeout(() => { this.unhighlightHandler(criterion.key); }, 5000),
        });
      }
    },
    /** Unhighlight a criterion table */
    unhighlightHandler(criterionKey) {
      this.highlightedCriterionKeys = this.highlightedCriterionKeys.filter(key => key !== criterionKey);
      this.unhighlightTimeouts = this.unhighlightTimeouts.filter(({ key }) => key !== criterionKey);
    },
    ...mapMutations({
      SHOW_SENSEI_CRITERIA,
    }),
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.vh-category-panel {
  background: #fff;
  display: flex;
  flex-flow: column;

  h2.basic-sub-title {
    margin: 0.5em;
  }

  .close-button {
    top: 0.3em;
    left: 0.3em;
  }

  .filters.settings-dropdown {
    background: #fff;

    > .settings-item {
      padding: 0.4em 2em;
      height: auto;
      background: transparent;
      margin-top: 0px;

    }
    &.expand {
      > .settings-item {
        background: $color-hoverbtn-lighter-blue;
      }
    }
    > .settings-item:hover {
      background: $color-hoverbtn-light-blue;
    }

    > .sd-overflow > div {
      padding: 0.4em 2em;
    }
    .editable-label-radio:not(:first-child) {
      margin-top: 0.3em;
    }
    .checkbox.sensei-cb {
      padding: 0.3em 0px;
    }
    .criticality-table {
      margin-top: 0.2em;

      .criticality-row {
        display: flex;
        width: 100%;
      }

      .criticality-col {
        flex: 1;
        min-width: 0px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        position: relative;
        border-style: solid;

        .settings-item {
          background: transparent;
          height: auto;
          padding: 0.3em 2.8em;
          margin-top: 0px;

          .expand-img {
            height: 1.2em;
          }
        }
        .sd-overflow > div {
          padding: 0.2em 0.8em;
        }
        .checkbox {
          position: absolute;
          top: 0;
          left: 0px;
          padding: 0px;
          background: rgba(0, 0, 0, 0.15);
          border-radius: 100px;
          transition: background 0.1s;

          &:hover {
            background: rgba(0, 0, 0, 0.3);
          }
          .tick {
            margin: 0.5em;
            width: 1.2em;
            height: 1.2em;
          }
        }
        .criticality-criterion {
          padding: 0.1em;
          cursor: pointer;

          &:hover {
            background: rgba(0, 0, 0, 0.1);
          }
        }
      }
    }
  }

  .vhcp-content.tabs {
    flex: 1;
    min-height: 0px;
    overflow: auto;

    > .content-wrapper {
      display: flex;
      min-height: 0px;

      .vhcp-selected-cat-panel-wrapper {
        padding-bottom: 30px;
        overflow: auto;
        width: 100%;

        .category-hint {
          @include sensei-texts;
          margin: 1em 2em;
          color: #0f3561;

          &.outro {
            margin-bottom: 4em;
          }
        }
      }
    }

    h3.category-title {
      text-align: center;
      margin-top: 0.5em;
    }
  }

  .vhcp-criteria-wrapper {
    display: flex;
    flex-flow: row wrap;
    justify-content: flex-start;

    .field-hint {
      margin: auto;
    }

    .vhcp-criterion-table {
      flex-basis: 47.9%;
      margin: 2em 1%;
      padding: 0.5%;
      box-sizing: border-box;
      transition: none;
      border-radius: 1em;

      &.highlight {
        box-shadow: 0px 0px 8px #bc7a14;
      }
      &:not(.highlight) {
        transition: box-shadow 5s;
      }
    }
  }
}

@media (max-width: $tablet-landscape) {
  .vh-category-panel {
    .categories {
      .category-item {
        flex-basis: 23.9%;
        margin: 0.5%;
      }
    }

    .vhcp-criteria-wrapper {
      .vhcp-criterion-table {
        flex-basis: 47.9%;
        margin: 1%;
        padding: 0.5%;
      }
    }
  }
}

@media (max-width: $smartphone-landscape) {
  .vh-category-panel {
    .categories {
      .category-item {
        flex-basis: 32%;
        margin: 0.5%;
      }
    }

    .vhcp-criteria-wrapper {
      .vhcp-criterion-table {
        flex-basis: 97.9%;
        margin: 1%;
        padding: 1%;
      }
    }
  }
}

@media (max-width: $smartphone-portrait) {
  .vh-category-panel {
    .categories {
      .category-item {
        flex-basis: 48.9%;
        margin: 0.5%;
      }
    }
  }
}

@media (max-width: $small-smartphone-portrait) {
  .vh-category-panel {
    .categories {
      .category-item {
        flex-basis: 99%;
        margin: 0.5%;
      }
    }
  }
}

</style>
